import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Sparkles, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useCredits } from "@/hooks/useCredits";
import { useSubscription } from "@/hooks/useSubscription";
import { Logo } from "@/components/Logo";

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [syncing, setSyncing] = useState(true);
  const { refetch: refetchCredits } = useCredits();
  const { refreshSubscription, subscription } = useSubscription();

  useEffect(() => {
    // Check if user has already seen this page
    const hasSeenWelcome = localStorage.getItem('subscription_welcomed');
    if (hasSeenWelcome) {
      navigate('/dashboard', { replace: true });
      return;
    }

    let retryCount = 0;
    const maxRetries = 3;
    
    const checkExistingSubscription = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return false;

        // Check if subscription already exists in database
        const { data: subData } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('status', 'active')
          .single();

        if (subData) {
          console.log("Subscription already synced:", subData);
          await refetchCredits();
          await refreshSubscription();
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error checking subscription:", error);
        return false;
      }
    };
    
    const syncSubscription = async () => {
      try {
        // First check if already synced
        const alreadySynced = await checkExistingSubscription();
        if (alreadySynced) {
          toast.success("Your subscription is active!");
          setSyncing(false);
          return;
        }

        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          toast.error("Please sign in again");
          navigate("/login");
          return;
        }

        const { data, error } = await supabase.functions.invoke('sync-stripe-subscription', {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (error) {
          console.error("Sync error response:", error);
          
          // Handle 404 errors (no subscription found yet in Stripe)
          if (error.message?.includes("No active subscription") || error.message?.includes("No Stripe customer")) {
            if (retryCount < maxRetries) {
              retryCount++;
              console.log(`Stripe subscription not ready yet, retrying (${retryCount}/${maxRetries})...`);
              toast.info(`Waiting for Stripe to process... (${retryCount}/${maxRetries})`);
              // Exponential backoff: 3s, 6s, 12s
              setTimeout(() => syncSubscription(), Math.pow(2, retryCount) * 1500);
              return;
            }
            
            // After max retries, check one more time if subscription was synced by webhook
            const finalCheck = await checkExistingSubscription();
            if (finalCheck) {
              toast.success("Your subscription is active!");
              setSyncing(false);
              return;
            }
            
            toast.error("Subscription processing is taking longer than expected. Please check your email or contact support.");
            setSyncing(false);
            return;
          }
          
          throw error;
        }

        console.log("Subscription synced successfully:", data);
        
        // Refetch credits and subscription status
        await refetchCredits();
        await refreshSubscription();
        
        toast.success("Your subscription is now active!");
        setSyncing(false);
      } catch (error: any) {
        console.error("Sync error:", error);
        
        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`Retrying after error (attempt ${retryCount}/${maxRetries})...`);
          toast.info(`Retrying... (${retryCount}/${maxRetries})`);
          setTimeout(() => syncSubscription(), Math.pow(2, retryCount) * 1500);
        } else {
          // Final check before giving up
          const finalCheck = await checkExistingSubscription();
          if (finalCheck) {
            toast.success("Your subscription is active!");
            setSyncing(false);
            return;
          }
          
          toast.error("Unable to verify subscription. Please refresh the page or contact support if you've completed payment.");
          setSyncing(false);
        }
      }
    };

    // Initial sync with a delay to allow Stripe webhook to process
    setTimeout(() => syncSubscription(), 2000);
  }, []);

  if (syncing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-background to-muted">
        <div className="text-center space-y-4">
          <Logo className="h-16 w-16 animate-pulse mx-auto" />
          <p className="text-muted-foreground">Setting up your subscription...</p>
        </div>
      </div>
    );
  }

  const planName = subscription?.plan_type === 'pro' ? 'Pro' : subscription?.plan_type === 'premium' ? 'Premium' : 'Starter';

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full border-primary/20 shadow-lg">
        <CardHeader className="text-center space-y-4 pb-8">
          <div className="mx-auto bg-primary/10 w-20 h-20 rounded-full flex items-center justify-center">
            <CheckCircle className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-3xl font-bold">
            Subscription Successful!
          </CardTitle>
          <CardDescription className="text-lg">
            Welcome to the {planName} plan. Your account has been upgraded and credits have been added.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="bg-muted/50 rounded-lg p-6 space-y-4">
            <div className="flex items-start gap-3">
              <Sparkles className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold mb-2">What's Next?</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Your credits have been added to your account</li>
                  <li>• Start creating your professional resume</li>
                  <li>• Generate tailored cover letters for your applications</li>
                  {(subscription?.plan_type === 'pro' || subscription?.plan_type === 'premium') && (
                    <>
                      <li>• Access premium features like LinkedIn Optimizer</li>
                      <li>• Use the Job Analyzer to match your resume perfectly</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={() => {
                localStorage.setItem('subscription_welcomed', 'true');
                navigate("/dashboard");
              }}
              className="flex-1"
              size="lg"
            >
              Go to Dashboard
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button 
              onClick={async () => {
                try {
                  const { data: { session } } = await supabase.auth.getSession();
                  if (!session) {
                    toast.error("Please sign in again");
                    return;
                  }

                  const { data, error } = await supabase.functions.invoke('customer-portal', {
                    headers: {
                      Authorization: `Bearer ${session.access_token}`,
                    },
                  });

                  if (error) throw error;
                  if (data?.url) {
                    window.open(data.url, '_blank');
                  }
                } catch (error: any) {
                  console.error("Portal error:", error);
                  toast.error("Failed to open billing portal");
                }
              }}
              variant="outline"
              size="lg"
              className="flex-1"
            >
              Manage Billing
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
